import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { PipelineRunner } from "@/components/PipelineRunner";
import {
  Plus, Play, ChevronLeft, GripVertical, X, Search, Zap,
  FolderSync, CheckCircle2, Clock, Coins, ChevronUp, ChevronDown, Loader2,
} from "lucide-react";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const AGENT_LIB = [
  { cat: "Audit", items: [
    { name: "Skill Gap Analyser", desc: "Learning objective gaps" },
    { name: "Standards Gap", desc: "Curriculum compliance check" },
    { name: "Content Quality", desc: "Readability & coverage" },
    { name: "Standards Aligner", desc: "Common Core / NGSS" },
    { name: "Accessibility Analyser", desc: "WCAG 2.1 gap report" },
    { name: "Duplicate Detector", desc: "Near-duplicate flagging" },
  ]},
  { cat: "Generation", items: [
    { name: "Assessment Generator", desc: "MCQs, short-answer, scenarios" },
    { name: "Pacing Guide", desc: "Curriculum pacing plans" },
    { name: "Answer Key Generator", desc: "Instructor materials" },
    { name: "Accessibility Remediator", desc: "Alt text, captions, WCAG" },
    { name: "Metadata Tagger", desc: "Topic taxonomy tagging" },
    { name: "Glossary Generator", desc: "Key terms & definitions" },
    { name: "Content Summarizer", desc: "Chapter summaries" },
    { name: "Flashcard Generator", desc: "Study cards per chapter" },
    { name: "Mastery Path Builder", desc: "Structured micro-courses" },
    { name: "TTS Audio Generator", desc: "Word-synced narration" },
    { name: "Objective Mapper", desc: "Learning objectives" },
    { name: "Solutions Manual", desc: "Instructor materials" },
    { name: "Translator / L10n", desc: "Multilingual preparation" },
  ]},
  { cat: "Operational", items: [
    { name: "Common Cartridge", desc: "IMS CC packaging" },
    { name: "Repository Sync", desc: "Pull from Drive, Box, S3" },
    { name: "Publish to Canvas", desc: "Direct LMS delivery" },
    { name: "Publish to Brightspace", desc: "D2L LMS delivery" },
    { name: "PDF Formatter", desc: "Layout & style normalisation" },
    { name: "EPUB Builder", desc: "EPUB3 packaging" },
    { name: "SCORM Wrapper", desc: "SCORM 1.2 / 2004" },
    { name: "Royalty Tracker", desc: "Cost flags & OER alternatives" },
    { name: "Notification Sender", desc: "Email & Slack alerts" },
  ]},
];

const AGENT_META = {
  "Assessment Generator": { time: "~4 min", tokens: "~12K", cost: "$0.18", output: "MCQs, short-answer, scenario-based items per chapter" },
  "Metadata Tagger": { time: "~2 min", tokens: "~6K", cost: "$0.09", output: "Topic taxonomy, subject tags, difficulty levels" },
  "Standards Aligner": { time: "~3 min", tokens: "~8K", cost: "$0.12", output: "Common Core / NGSS alignment mappings" },
  "A11y Remediator": { time: "~5 min", tokens: "~15K", cost: "$0.22", output: "Alt text, captions, reading order fixes" },
  "Glossary Generator": { time: "~2 min", tokens: "~5K", cost: "$0.08", output: "Key terms, definitions, cross-references" },
  "TTS Audio": { time: "~8 min", tokens: "~3K", cost: "$0.45", output: "Word-synced narration audio files" },
  "Content Summarizer": { time: "~2 min", tokens: "~4K", cost: "$0.06", output: "Chapter summaries, key takeaways" },
  "Mastery Path Builder": { time: "~3 min", tokens: "~7K", cost: "$0.11", output: "Structured learning path with modules" },
  "Flashcard Generator": { time: "~2 min", tokens: "~4K", cost: "$0.06", output: "Study flashcards per chapter" },
  "A11y Analyser": { time: "~1 min", tokens: "~3K", cost: "$0.05", output: "WCAG 2.1 gap report with severity scores" },
  "Common Cartridge": { time: "~1 min", tokens: "~1K", cost: "$0.02", output: "IMS CC 1.3 package" },
  "Publish to Canvas": { time: "~30 sec", tokens: "~500", cost: "$0.01", output: "Direct Canvas LMS delivery" },
  "Skill Gap Analyser": { time: "~2 min", tokens: "~5K", cost: "$0.08", output: "Learning objective coverage gaps" },
  "Standards Gap": { time: "~2 min", tokens: "~6K", cost: "$0.09", output: "Curriculum compliance gaps" },
  "Pacing Guide": { time: "~3 min", tokens: "~6K", cost: "$0.09", output: "Curriculum pacing plans" },
  "Answer Key Generator": { time: "~2 min", tokens: "~4K", cost: "$0.06", output: "Instructor answer keys" },
};
const defaultMeta = { time: "~2 min", tokens: "~4K", cost: "$0.06", output: "AI-generated output" };

export const Pipelines = () => {
  const [view, setView] = useState("gallery");
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);
  const [pipelineAgents, setPipelineAgents] = useState([]);
  const [agentSearch, setAgentSearch] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [builderName, setBuilderName] = useState("");
  const [saving, setSaving] = useState(false);
  const [runningPack, setRunningPack] = useState(null);

  const fetchPacks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orchestrations`, { withCredentials: true });
      setPacks(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Failed to load orchestrations:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPacks(); }, [fetchPacks]);

  const openBuilder = (pack) => {
    setSelectedPack(pack);
    setPipelineAgents(pack ? pack.agents?.map((a, i) => ({ id: i, name: a })) : []);
    setBuilderName(pack?.name || "New Orchestration");
    setView("builder");
    setShowPreview(false);
  };

  const addAgent = (name) => {
    setPipelineAgents((prev) => [...prev, { id: Date.now(), name }]);
  };

  const removeAgent = (id) => {
    setPipelineAgents((prev) => prev?.filter((a) => a.id !== id));
  };

  const moveAgent = (from, to) => {
    setPipelineAgents((prev) => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const handleDragStart = (i) => setDragIdx(i);
  const handleDragOver = (e, i) => {
    e.preventDefault();
    if (dragIdx !== null && dragIdx !== i) { moveAgent(dragIdx, i); setDragIdx(i); }
  };
  const handleDragEnd = () => setDragIdx(null);

  const handleSave = async () => {
    setSaving(true);
    try {
      const agentNames = pipelineAgents?.map(a => a.name);
      if (selectedPack?.id) {
        await axios.put(`${API}/orchestrations/${selectedPack.id}`, { name: builderName, agents: agentNames }, { withCredentials: true });
      } else {
        await axios.post(`${API}/orchestrations`, { name: builderName, agents: agentNames }, { withCredentials: true });
      }
      await fetchPacks();
      setView("gallery");
    } catch (e) {
      console.error("Failed to save orchestration:", e);
    } finally {
      setSaving(false);
    }
  };

  const totalTime = pipelineAgents.reduce((acc, a) => {
    const m = AGENT_META[a.name] || defaultMeta;
    return acc + (parseFloat(m.time.replace(/[^0-9.]/g, "")) || 2);
  }, 0);
  const totalCost = pipelineAgents.reduce((acc, a) => {
    const m = AGENT_META[a.name] || defaultMeta;
    return acc + parseFloat(m.cost.replace("$", ""));
  }, 0);
  const totalTokens = pipelineAgents.reduce((acc, a) => {
    const m = AGENT_META[a.name] || defaultMeta;
    return acc + (parseFloat(m.tokens.replace(/[^0-9.]/g, "")) || 4);
  }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="pipelines-loading">
        <Loader2 className="w-6 h-6 animate-spin text-foreground/45" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="pipelines-page">

      {runningPack && (
        <PipelineRunner
          orchestration={runningPack}
          onClose={() => setRunningPack(null)}
          onComplete={() => fetchPacks()}
        />
      )}

      {view === "gallery" ? (
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading font-bold text-lg text-foreground">Orchestrations</h2>
            <div className="flex gap-2">
              <button className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border/60 text-foreground/50 hover:text-foreground">Import JSON</button>
              <button onClick={() => openBuilder(null)} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#014d7d] text-white flex items-center gap-1.5" data-testid="new-orchestration">
                <Plus className="w-3.5 h-3.5" /> New Orchestration
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="pack-grid">
            {packs?.map((p) => (
              <div key={p.id} className="bg-white border border-border/60 rounded-2xl overflow-hidden hover:shadow-soft" data-testid={`pack-${p.id}`}>
                <div className={`h-1.5 bg-gradient-to-r ${p.color}`} />
                <div className="p-5">
                  <h3 className="text-sm font-bold text-foreground mb-1.5">{p.name}</h3>
                  <p className="text-xs text-foreground/40 leading-relaxed mb-3">{p.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {(p.agents || [])?.map((a) => (
                      <span key={a} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-foreground/[0.04] text-foreground/50">{a}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between px-5 py-3 border-t border-border/30">
                  <span className="text-[10px] text-foreground/50">{(p.agents || []).length} agents · Last run {p.last_run || "Never"}</span>
                  <div className="flex gap-1.5">
                    <button onClick={() => openBuilder(p)} className="text-xs font-semibold px-3 py-1 rounded-lg border border-border/60 text-foreground/50 hover:text-foreground" data-testid={`edit-pack-${p.id}`}>Edit</button>
                    <button onClick={() => setRunningPack(p)} className="text-xs font-semibold px-3 py-1 rounded-lg bg-[#014d7d] text-white flex items-center gap-1" data-testid={`run-pack-${p.id}`}>
                      <Play className="w-3 h-3" fill="currentColor" /> Run
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* New pack card */}
            <button onClick={() => openBuilder(null)} className="border-2 border-dashed border-border/40 rounded-2xl flex flex-col items-center justify-center py-12 text-foreground/45 hover:text-foreground/40 hover:border-border/60" data-testid="new-pack-card">
              <Plus className="w-8 h-8 mb-2" />
              <span className="text-sm font-semibold">New Orchestration</span>
              <span className="text-xs mt-1">Build from scratch or clone</span>
            </button>
          </div>
        </div>
      ) : (
        /* Builder View */
        <div className="flex flex-col flex-1 overflow-hidden" data-testid="builder-view">
          <div className="flex items-center gap-3 px-5 py-3 border-b border-border/40 bg-white shrink-0">
            <button onClick={() => setView("gallery")} className="text-xs font-semibold text-foreground/40 hover:text-foreground flex items-center gap-1" data-testid="back-to-gallery">
              <ChevronLeft className="w-4 h-4" /> Orchestrations
            </button>
            <div className="w-px h-5 bg-border/40 mx-1" />
            <input
              value={builderName}
              onChange={(e) => setBuilderName(e.target.value)}
              className="text-sm font-bold text-foreground outline-none bg-transparent flex-1"
              placeholder="Orchestration name..."
              data-testid="builder-title"
            />
            <span className="text-[11px] text-foreground/40 px-2.5 py-1 bg-[#014d7d]/5 border border-[#014d7d]/15 rounded-lg">
              <Zap className="w-3 h-3 inline mr-1" />{pipelineAgents.length} agents
            </span>
            <button onClick={handleSave} disabled={saving} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-border/60 text-foreground/50 hover:text-foreground disabled:opacity-40" data-testid="save-orchestration">
              {saving ? "Saving..." : "Save"}
            </button>
            <button className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#014d7d] text-white flex items-center gap-1.5">
              <Play className="w-3 h-3" fill="currentColor" /> Run Pipeline
            </button>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Agent Library */}
            <div className="w-64 border-r border-border/40 flex flex-col bg-white shrink-0">
              <div className="p-3 border-b border-border/40">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-foreground/45" />
                  <input value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)} placeholder="Search agents..." className="w-full h-8 pl-8 pr-3 text-xs border border-border/60 rounded-lg outline-none bg-white placeholder:text-foreground/45" data-testid="agent-search" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {AGENT_LIB?.map((cat) => {
                  const items = cat.items?.filter((a) => !agentSearch || a.name.toLowerCase().includes(agentSearch.toLowerCase()));
                  if (items.length === 0) return null;
                  return (
                    <div key={cat.cat}>
                      <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider px-2 py-2 mt-2">{cat.cat}</p>
                      {items?.map((a) => (
                        <button
                          key={a.name}
                          onClick={() => addAgent(a.name)}
                          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-foreground/[0.03]"
                          data-testid={`agent-${a.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <div className="w-7 h-7 rounded-lg bg-[#014d7d]/8 flex items-center justify-center shrink-0"><Zap className="w-3 h-3 text-[#014d7d]" /></div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-foreground truncate">{a.name}</p>
                            <p className="text-[10px] text-foreground/50 truncate">{a.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Canvas + Preview */}
            <div className="flex-1 flex overflow-hidden">
              <div className="flex-1 overflow-y-auto bg-[#f8f9fb] flex flex-col items-center py-8 px-6" data-testid="builder-canvas">
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border/60 bg-white shadow-sm text-xs font-semibold text-foreground/60">
                  <FolderSync className="w-4 h-4" /> Content Repository
                </div>
                <div className="w-px h-6 bg-border/40" />

                {pipelineAgents.length === 0 ? (
                  <div className="w-64 py-8 border-2 border-dashed border-border/30 rounded-2xl flex flex-col items-center text-foreground/45">
                    <Plus className="w-6 h-6 mb-2" />
                    <p className="text-xs font-semibold">Click agents from the library</p>
                  </div>
                ) : (
                  pipelineAgents?.map((a, i) => {
                    const meta = AGENT_META[a.name] || defaultMeta;
                    return (
                      <div key={a.id}>
                        <div
                          draggable
                          onDragStart={() => handleDragStart(i)}
                          onDragOver={(e) => handleDragOver(e, i)}
                          onDragEnd={handleDragEnd}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border bg-white shadow-sm group cursor-grab active:cursor-grabbing ${
                            dragIdx === i ? "border-[#014d7d] ring-2 ring-[#014d7d]/10 opacity-70" : "border-[#014d7d]/20"
                          }`}
                          data-testid={`pipeline-agent-${i}`}
                        >
                          <GripVertical className="w-3.5 h-3.5 text-foreground/15 group-hover:text-foreground/50" />
                          <div className="w-7 h-7 rounded-lg bg-[#014d7d]/8 flex items-center justify-center"><Zap className="w-3 h-3 text-[#014d7d]" /></div>
                          <div className="min-w-0 flex-1">
                            <span className="text-xs font-semibold text-foreground">{a.name}</span>
                            <span className="text-[10px] text-foreground/50 ml-2">{meta.time}</span>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                            {i > 0 && <button onClick={() => moveAgent(i, i - 1)} className="w-5 h-5 rounded text-foreground/45 hover:text-foreground hover:bg-foreground/5 flex items-center justify-center"><ChevronUp className="w-3 h-3" /></button>}
                            {i < pipelineAgents.length - 1 && <button onClick={() => moveAgent(i, i + 1)} className="w-5 h-5 rounded text-foreground/45 hover:text-foreground hover:bg-foreground/5 flex items-center justify-center"><ChevronDown className="w-3 h-3" /></button>}
                            <button onClick={() => removeAgent(a.id)} className="w-5 h-5 rounded text-foreground/15 hover:text-red-500 hover:bg-red-50 flex items-center justify-center"><X className="w-3 h-3" /></button>
                          </div>
                        </div>
                        <div className="w-px h-4 bg-border/40 mx-auto" />
                      </div>
                    );
                  })
                )}

                <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border/60 bg-white shadow-sm text-xs font-semibold text-foreground/60">
                  <CheckCircle2 className="w-4 h-4" /> Review Queue &rarr; Outputs
                </div>

                {pipelineAgents.length > 0 && (
                  <button
                    onClick={() => setShowPreview((p) => !p)}
                    className="mt-6 text-xs font-semibold px-4 py-2 rounded-xl border border-[#014d7d]/20 text-[#014d7d] hover:bg-[#014d7d]/5 flex items-center gap-2"
                    data-testid="toggle-preview"
                  >
                    {showPreview ? "Hide" : "Show"} Execution Preview
                  </button>
                )}
              </div>

              {/* Execution Preview Panel */}
              {showPreview && pipelineAgents.length > 0 && (
                <div className="w-72 border-l border-border/40 bg-white shrink-0 overflow-y-auto" data-testid="execution-preview">
                  <div className="px-5 py-4 border-b border-border/40">
                    <h3 className="font-heading font-bold text-sm text-foreground mb-3">Execution Preview</h3>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-[#f8f9fb] rounded-xl p-2.5 text-center">
                        <Clock className="w-3.5 h-3.5 text-foreground/50 mx-auto mb-1" />
                        <p className="text-sm font-heading font-bold text-foreground">~{Math.round(totalTime)}m</p>
                        <p className="text-[9px] text-foreground/50 font-semibold">Est. Time</p>
                      </div>
                      <div className="bg-[#f8f9fb] rounded-xl p-2.5 text-center">
                        <Zap className="w-3.5 h-3.5 text-foreground/50 mx-auto mb-1" />
                        <p className="text-sm font-heading font-bold text-foreground">~{Math.round(totalTokens)}K</p>
                        <p className="text-[9px] text-foreground/50 font-semibold">Tokens</p>
                      </div>
                      <div className="bg-[#f8f9fb] rounded-xl p-2.5 text-center">
                        <Coins className="w-3.5 h-3.5 text-foreground/50 mx-auto mb-1" />
                        <p className="text-sm font-heading font-bold text-foreground">${totalCost.toFixed(2)}</p>
                        <p className="text-[9px] text-foreground/50 font-semibold">Est. Cost</p>
                      </div>
                    </div>
                  </div>
                  <div className="px-5 py-3 space-y-3">
                    <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider">Step-by-step</p>
                    {pipelineAgents?.map((a, i) => {
                      const m = AGENT_META[a.name] || defaultMeta;
                      return (
                        <div key={a.id} className="border border-border/30 rounded-xl p-3" data-testid={`preview-step-${i}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-5 h-5 rounded-full bg-[#014d7d] text-white flex items-center justify-center text-[9px] font-bold">{i + 1}</div>
                            <span className="text-xs font-semibold text-foreground flex-1">{a.name}</span>
                          </div>
                          <div className="flex items-center gap-3 text-[10px] text-foreground/40 mb-2">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {m.time}</span>
                            <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {m.tokens}</span>
                            <span className="flex items-center gap-1"><Coins className="w-3 h-3" /> {m.cost}</span>
                          </div>
                          <p className="text-[11px] text-foreground/50 leading-relaxed">{m.output}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
